import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  SidebarSubmenu,
  SidebarSubmenuItem,
  Link,
} from '@backstage/core-components';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import CategoryIcon from '@mui/icons-material/Category';
import SecurityIcon from '@mui/icons-material/Security';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SpeedIcon from '@mui/icons-material/Speed';
import KeyIcon from '@mui/icons-material/Key';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import InventoryIcon from '@mui/icons-material/Inventory';
import HistoryIcon from '@mui/icons-material/History';
import GavelIcon from '@mui/icons-material/Gavel';
import logoPlatform from '../assets/narekomu-ai-platform.webp';
import { DemoScenarioBanner } from './DemoScenarioBanner';

const useSidebarLogoStyles = makeStyles({
  root: {
    height: 3 * sidebarConfig.logoHeight + 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginBottom: -4,
    padding: '0 16px',
  },
  link: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textDecoration: 'none',
  },
  logoPill: {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.97)',
    borderRadius: 8,
    padding: '8px 10px',
    transition: 'width 0.2s ease',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  },
  logoImage: {
    height: 26,
    objectFit: 'contain',
    objectPosition: 'left center',
    display: 'block',
  },
  subBrand: {
    color: '#D9E2EC',  // Blue Grey 800 — 視認性向上
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.5px',
    marginTop: 6,
    paddingLeft: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    transition: 'opacity 0.2s ease',
  },
});

function SidebarLogo() {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        <div
          className={classes.logoPill}
          style={{ width: isOpen ? 192 : 36 }}
        >
          <img
            src={logoPlatform}
            alt="ナレコムAI Platform"
            className={classes.logoImage}
            style={{ width: isOpen ? 180 : 24 }}
          />
        </div>
        <span
          className={classes.subBrand}
          style={{ opacity: isOpen ? 1 : 0 }}
        >
          Operations Hub
        </span>
      </Link>
    </div>
  );
}

/**
 * Root - Portal shell with sidebar navigation.
 *
 * Navigation structure:
 * - Home (overview dashboard)
 * - Service Catalog
 * - SRE Dashboard (with submenu for each domain)
 * - SRE Chat (full-page mode)
 * - Governance (FT-010: roles, audit, changes)
 * - Resources (FT-008: ON/OFF management)
 * - Decision Log (FT-009: history)
 * - TechDocs
 * - Search
 */
export function Root({ children }: PropsWithChildren<{}>) {
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />

        <SidebarGroup label="メニュー" icon={<MenuIcon />}>
          <SidebarItem icon={HomeIcon} to="/" text="ホーム" />
          <SidebarItem icon={CategoryIcon} to="catalog" text="サービス一覧" />

          <SidebarDivider />

          {/* SRE Dashboard with sub-menu */}
          <SidebarItem icon={DashboardIcon} text="運用状況の一覧">
            <SidebarSubmenu title="運用状況の一覧">
              <SidebarSubmenuItem
                title="概要"
                to="sre-dashboard/overview"
                icon={AssessmentIcon}
              />
              <SidebarSubmenuItem
                title="セキュリティ"
                to="sre-dashboard/security"
                icon={SecurityIcon}
              />
              <SidebarSubmenuItem
                title="コスト"
                to="sre-dashboard/cost"
                icon={AttachMoneyIcon}
              />
              <SidebarSubmenuItem
                title="パフォーマンス"
                to="sre-dashboard/performance"
                icon={SpeedIcon}
              />
              <SidebarSubmenuItem
                title="アクセス権限管理"
                to="sre-dashboard/iam"
                icon={KeyIcon}
              />
              <SidebarSubmenuItem
                title="ログ"
                to="sre-dashboard/logs"
                icon={SearchIcon}
              />
            </SidebarSubmenu>
          </SidebarItem>

          <SidebarItem icon={ChatIcon} to="sre-chat" text="Super Agent SRE" />

          <SidebarDivider />

          {/* Governance (FT-010) */}
          <SidebarItem icon={AdminPanelSettingsIcon} text="ガバナンス">
            <SidebarSubmenu title="ガバナンス">
              <SidebarSubmenuItem
                title="ロール管理"
                to="governance/roles"
                icon={AdminPanelSettingsIcon}
              />
              <SidebarSubmenuItem
                title="監査証跡"
                to="governance/audit-log"
                icon={GavelIcon}
              />
              <SidebarSubmenuItem
                title="変更履歴"
                to="governance/changes"
                icon={HistoryIcon}
              />
            </SidebarSubmenu>
          </SidebarItem>

          {/* Resources (FT-008) */}
          <SidebarItem
            icon={InventoryIcon}
            to="resources"
            text="リソース管理"
          />

          {/* Decision Log (FT-009) */}
          <SidebarItem
            icon={HistoryIcon}
            to="decisions"
            text="意思決定ログ"
          />

          <SidebarDivider />

          <SidebarItem
            icon={DescriptionIcon}
            to="docs"
            text="技術ドキュメント"
          />
          <SidebarItem icon={SearchIcon} to="search" text="検索" />
        </SidebarGroup>

        <SidebarSpace />

        <SidebarGroup label="設定" icon={<UserSettingsSignInAvatar />}>
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>

      <DemoScenarioBanner />
      {children}
    </SidebarPage>
  );
}
